/**
 * Brief — the enterprise commercial brief (Redpill Software × Ontoligent).
 *
 * Ported from the standalone backend `commercial/index.html` into the FE design
 * system so it can be shared on demand. It is intentionally HIDDEN from the
 * marketing nav (no entry in MarketingNav LINKS) but reachable by direct URL at
 * /brief. Forms submit through the resilient contact chain; the pricing CTA
 * opens the enterprise engagement dialog.
 */
import { useState } from "react";
import {
  ArrowRight, Compass, Boxes, Search, Globe, GitMerge, Activity, Zap,
  GraduationCap, ShieldCheck, Check, Loader2, FileDown,
} from "lucide-react";
import { PricingEngagement } from "@/components/PricingEngagement";
import { submitContact, type ContactResult } from "@/lib/contact";

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">{children}</p>
  );
}

function GradientText({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-gradient-to-r from-accent to-good bg-clip-text text-transparent">{children}</span>
  );
}

const PRODUCT = [
  { Icon: Compass, t: "Architecture Studio", d: "A Solution-Architect Copilot interviews you and recommends + deploys the ontology, retrieval, pipeline, agents, governance and evals." },
  { Icon: Boxes, t: "Ontology as code", d: "Versioned entity/relation types, SHACL validation, diff & rollback — the extraction contract agents must obey." },
  { Icon: Search, t: "Auto-routed retrieval", d: "A router picks vector / graph / GraphRAG per question and shows why. ACL-filtered before fusion." },
  { Icon: Globe, t: "Language-agnostic", d: "Any dialect, accent or transcription variant resolves to one canonical concept before embedding." },
  { Icon: GitMerge, t: "Entity resolution", d: "Sub-threshold duplicate merges are queued for human review — never applied silently or destructively." },
  { Icon: Activity, t: "Behavioral events", d: "Signals → behavior graph → features → triggers that fire agents or actions: the loop that turns knowledge into action." },
  { Icon: Zap, t: "Closed-loop actions", d: "Approval-gated, dry-runnable, idempotent and reversible write-back to your systems with full decision→action lineage." },
  { Icon: GraduationCap, t: "Eval gates", d: "No ontology, retrieval or agent change reaches production if it degrades the golden set. It improves, never silently regresses." },
  { Icon: ShieldCheck, t: "Governance & budget", d: "Per-workspace daily budget caps at the gateway, audit, lineage, ABAC, PII redaction, confidence scoring." },
];

const FLOW = [
  { n: "1", t: "Business problem", d: "A short, jargon-free discovery interview captures industry, objective, sources, volume, real-time needs and explainability." },
  { n: "2", t: "Knowledge assessment", d: "What do you have, where, and how trustworthy is it? Event-quality and source diagnostics are part of the platform." },
  { n: "3", t: "Architecture recommendation", d: "Knowledge / Graph / Action path chosen with a confidence score and a plain-language rationale." },
  { n: "4", t: "Ontology & blueprint", d: "Entity types, retrieval profile, DAG, agent topology, governance model and eval set — editable, then deployed as real rows." },
  { n: "5", t: "Governed operation", d: "Connect data, run pipelines, ask questions, propose actions — with budget, lineage, confidence and audit on every run." },
  { n: "6", t: "Business outcomes", d: "Faster cycle times, a defensible audit trail, and a system that gets smarter from feedback through eval-gated promotion." },
];

const COMPARE = [
  ["Palantir Foundry", "Operational ontology at scale", "Self-serve Architecture Studio; BYO-LLM; transparent, packageable, lower-cost to start."],
  ["Stardog / graph DBs", "Knowledge graph & reasoning", "End-to-end pipeline, agents, governed actions and evals — not just storage."],
  ["Databricks / Fabric", "Data & ML platform", "Domain ontology, explainable retrieval, human-in-the-loop action governance."],
  ["ServiceNow / SaaS AI", "Workflow automation", "Cross-system knowledge graph + cited reasoning, not siloed point automation."],
  ["Generic RAG / LangChain", "Quick prototypes", "Governance, lineage, ABAC, budget caps, eval gates, reversible actions — production trust."],
];

const ROI = [
  ["↓ cycle time", "Multi-week evidence assembly and triage collapse to hours with a cited knowledge graph."],
  ["↓ risk cost", "Lineage, confidence and audit cut the cost of bad data, rework and compliance findings."],
  ["↓ build cost", "The Architecture Studio replaces months of bespoke RAG/graph engineering with a deployable blueprint."],
  ["↑ defensibility", "100% cited answers and reversible, approval-gated actions a CFO and a regulator both sign off on."],
];

const COMMERCIAL = [
  ["2-week paid pilot", "One high-value use case on your data with a fixed fee and a clear success metric."],
  ["Knowledge assessment", "A short, paid discovery that produces a ready-to-deploy architecture blueprint and ROI model."],
  ["Industry starter packs", "Pre-built ontologies + agents for insurance, banking and healthcare."],
  ["Outcome-based pricing", "Tie part of the fee to a measurable result to de-risk the buying decision."],
  ["Champion enablement", "Equip an internal sponsor with this brief, an ROI calculator and a security pack."],
  ["Offline eval build", "A self-contained desktop build to satisfy security review with zero network egress."],
];

function AccessForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<ContactResult | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    const res = await submitContact({
      kind: "access",
      name, email, company,
      subject: `Executive briefing request — ${company || "new"}`,
      message,
      meta: { source: "commercial brief" },
    });
    setResult(res);
    setSending(false);
  }

  const inputCls =
    "w-full rounded-lg border border-border bg-bg/60 px-3 py-2 text-sm outline-none transition focus:border-accent/60 focus:ring-2 focus:ring-accent/15";

  if (result?.ok) {
    return (
      <div className="card flex flex-col items-center p-8 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full border border-good/30 bg-good/10 text-good">
          <Check size={22} />
        </div>
        <h3 className="mt-3 text-lg font-semibold">Request received</h3>
        <p className="mt-2 max-w-sm text-sm text-muted">
          {result.method === "mailto"
            ? "Your request is ready in your email client — send it and we'll reply within one business day."
            : "We've logged your request and a Redpill architect will reply within one business day."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card p-6 sm:p-7">
      <h3 className="text-xl font-semibold tracking-tight">Book an executive briefing</h3>
      <p className="mt-1.5 text-[14px] text-muted">
        A 45-minute session: your problem, a live Architecture Studio recommendation, the security model,
        and an ROI estimate sized to your volumes.
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">Name</span>
          <input className={inputCls} required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">Work email</span>
          <input className={inputCls} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="block sm:col-span-2"><span className="mb-1 block text-[12px] font-medium text-text/80">Company</span>
          <input className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)} /></label>
        <label className="block sm:col-span-2"><span className="mb-1 block text-[12px] font-medium text-text/80">What would you like to cover?</span>
          <textarea className={`${inputCls} min-h-[72px] resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
      </div>
      <button className="btn-primary mt-5 w-full justify-center py-2.5" type="submit" disabled={sending}>
        {sending ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <>Request briefing <ArrowRight size={15} /></>}
      </button>
      <p className="mt-2.5 text-center text-[11px] text-muted">Confidential. We reply within one business day.</p>
    </form>
  );
}

export function Brief() {
  const [engageOpen, setEngageOpen] = useState(false);

  return (
    <div className="space-y-20 pb-24">
      {/* HERO */}
      <section className="pt-10">
        <span className="chip">Enterprise Intelligence Platform · AI Architecture Studio</span>
        <h1 className="mt-5 max-w-4xl text-4xl font-bold leading-[1.12] tracking-tight md:text-6xl">
          Turn regulated knowledge into <GradientText>governed, explainable decisions</GradientText>.
        </h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-muted">
          <strong className="text-text">Ontoligent</strong> is the flagship product of{" "}
          <strong className="text-text">Redpill Software</strong> — a system of record that designs its own
          architecture from your business problem, then runs a closed loop:
          ontology → retrieval → agents → governed action → audit → improvement.
          Use it yourself, or have our team run it on your behalf.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button className="btn-primary" onClick={() => setEngageOpen(true)}>
            Book an executive briefing <ArrowRight size={15} />
          </button>
          <a href="#how" className="btn-ghost">See how it works</a>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[["Describe", "the problem — not the architecture"], ["Approve", "a recommended, editable blueprint"],
            ["Govern", "budget, lineage, ABAC, evals"], ["Act", "approval-gated, reversible write-back"]].map(([t, d]) => (
            <div key={t} className="card p-5">
              <div className="text-2xl font-bold"><GradientText>{t}</GradientText></div>
              <div className="mt-2 text-sm leading-relaxed text-muted">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section>
        <Label>The business problem</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Knowledge is trapped in documents, not systems</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          Enterprises sit on decades of documents, decisions and expertise — yet most AI projects stall because they are
          ungoverned, unexplainable, or impossible to trust in a regulated setting. Generic RAG chatbots hallucinate,
          can't show lineage, ignore permissions, and never close the loop into action.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[["Knowledge is trapped", "Critical context lives in PDFs, tickets, call recordings and people's heads — not in a computable, queryable model."],
            ["AI you can't defend", "A regulator or auditor asks \"why did the system say that?\" and there is no lineage, no confidence, no audit trail."],
            ["No closed loop", "Insights sit in a dashboard. Nothing acts on them safely, with approval, idempotency and the ability to reverse."]].map(([t, d]) => (
            <div key={t} className="card p-6">
              <div className="text-lg font-semibold tracking-tight">{t}</div>
              <p className="mt-2.5 text-[15px] leading-relaxed text-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT */}
      <section id="product">
        <Label>What Ontoligent is</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">An Enterprise Intelligence Platform</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          With an <strong className="text-text">AI Architecture Studio</strong> on top. You never have to know
          "I need GraphRAG + entity resolution + a knowledge graph." You describe the problem; Ontoligent designs the
          architecture, you approve it, and every step is explainable, governed and reversible.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {PRODUCT.map(({ Icon, t, d }) => (
            <div key={t} className="card p-6">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-accent/20 bg-accent/10 text-accent">
                <Icon size={18} />
              </span>
              <div className="mt-4 font-semibold tracking-tight">{t}</div>
              <p className="mt-2 text-[15px] leading-relaxed text-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section id="how">
        <Label>The process</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">How an engagement flows</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          The same path every time — from a business problem to a governed, closed-loop system.
        </p>
        <ol className="mt-8 grid gap-4 md:grid-cols-2">
          {FLOW.map(({ n, t, d }) => (
            <li key={n} className="card flex gap-5 p-6">
              <span className="text-3xl font-bold leading-none"><GradientText>{n}</GradientText></span>
              <div>
                <div className="font-semibold tracking-tight">{t}</div>
                <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{d}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="card mt-6 p-6">
          <Label>Worked example — claims triage (insurance)</Label>
          <p className="text-[15px] leading-relaxed text-muted">
            A claims lead goes from <em>"I need to cut processing time"</em> to a deployed, governed system that found a fraud
            signal (a duplicate claimant resolved across a form and a call transcript), opened a real ticket
            <em> after human approval</em>, and improved from a single thumbs-down — without anyone choosing a database,
            an ontology format, or a retrieval strategy by hand.
          </p>
        </div>
      </section>

      {/* COMPARE */}
      <section>
        <Label>Where Ontoligent fits</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Not another data lake or generic chatbot</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          The governed layer that makes enterprise knowledge computable and actionable.
        </p>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-muted">
                <th className="border-b border-border py-3 pr-6 font-medium">Approach</th>
                <th className="border-b border-border py-3 pr-6 font-medium">Strength</th>
                <th className="border-b border-border py-3 font-medium">Gap Ontoligent fills</th>
              </tr>
            </thead>
            <tbody className="text-muted">
              {COMPARE.map(([a, s, g]) => (
                <tr key={a}>
                  <td className="border-b border-border py-3.5 pr-6 font-semibold text-text">{a}</td>
                  <td className="border-b border-border py-3.5 pr-6">{s}</td>
                  <td className="border-b border-border py-3.5">{g}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ROI */}
      <section>
        <Label>The ROI model</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Conservative, defensible drivers</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">Sized to your volumes during the briefing.</p>
        <div className="mt-8 grid gap-5 md:grid-cols-4">
          {ROI.map(([t, d]) => (
            <div key={t} className="card p-6">
              <div className="text-2xl font-bold"><GradientText>{t}</GradientText></div>
              <p className="mt-2.5 text-[15px] leading-relaxed text-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY */}
      <section>
        <Label>Credibility & security</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Answered before you ask</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          We pre-empt security and procurement questions with deployment options that fit your risk posture.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <div className="card p-6">
            <div className="font-semibold tracking-tight">Deployment options</div>
            <ul className="mt-3 space-y-2.5 text-[15px] leading-relaxed text-muted">
              <li>• <strong className="text-text">Managed cloud</strong> — tenant-isolated, fastest to value.</li>
              <li>• <strong className="text-text">Your cloud (VPC / private)</strong> — runs inside your perimeter and egress allow-list.</li>
              <li>• <strong className="text-text">On-prem / air-gapped</strong> — for the most regulated data, with local model backends.</li>
              <li>• <strong className="text-text">Offline desktop evaluation</strong> — a self-contained build for zero network egress review.</li>
            </ul>
          </div>
          <div className="card p-6">
            <div className="font-semibold tracking-tight">Security posture</div>
            <ul className="mt-3 space-y-2.5 text-[15px] leading-relaxed text-muted">
              <li>• <strong className="text-text">Bring-your-own model endpoints</strong> — route calls to customer-approved providers under their data-use terms.</li>
              <li>• <strong className="text-text">ABAC + permission-aware retrieval</strong> — access control applied before fusion.</li>
              <li>• <strong className="text-text">PII redaction</strong> pre-flight; full audit, lineage and confidence on every run.</li>
              <li>• <strong className="text-text">Budget caps & eval gates</strong> — no runaway spend, no silent regression.</li>
              <li>• Control mapping can support a customer's <strong className="text-text">SOC 2 / ISO 27001</strong> program; Ontoligent is not currently certified.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ENGAGE */}
      <section>
        <Label>Engage</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Two ways to work with Redpill</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <div className="card p-7">
            <span className="chip">Product</span>
            <div className="mt-4 text-xl font-semibold tracking-tight">License Ontoligent</div>
            <p className="mt-2.5 text-[15px] leading-relaxed text-muted">
              Your team uses the platform directly. We provide onboarding, domain packs, and support. Ideal when you have
              a data/ML team and want to own the capability.
            </p>
          </div>
          <div className="card p-7">
            <span className="chip">Done-for-you</span>
            <div className="mt-4 text-xl font-semibold tracking-tight">We run it on your behalf</div>
            <p className="mt-2.5 text-[15px] leading-relaxed text-muted">
              Contract <strong className="text-text">Redpill Software</strong> as a consultancy. We design the ontology,
              deploy the architecture, and operate the governed system — you consume outcomes.
            </p>
          </div>
        </div>
        <h3 className="mt-12 text-2xl font-bold tracking-tight">Commercial ideas to accelerate adoption</h3>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {COMMERCIAL.map(([t, d]) => (
            <div key={t} className="card p-6">
              <div className="font-semibold tracking-tight">{t}</div>
              <p className="mt-2.5 text-[15px] leading-relaxed text-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section>
        <Label>Pricing</Label>
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Priced on value, not seats</h2>
        <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-muted">
          Like Palantir, we don't sell per-seat SaaS for production decision systems. The expensive part of their model is
          a Forward Deployed Engineer who designs your architecture — our <strong className="text-text">Architecture
          Studio</strong> automates the first 80% of that work, and a Redpill architect reviews the rest. Paid plans start
          with a short scoping conversation, and pricing tracks hosting, LLM ownership, data volume, workflows in
          production and compliance.
        </p>
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {[["1 · Land", "Paid proof-of-value", "A 2-week pilot or Knowledge Assessment on your data, one high-value use case, fixed fee, pre-agreed success metric. Deliverable: a deployed, reviewed architecture + ROI model.", "Indicative €15k–€60k"],
            ["2 · Run", "Platform + consumption", "Negotiated, not a price list. Tracks hosting (self vs. Our host), LLM (BYO vs. managed), data volume, production workflows and compliance regime. Consultant review included.", ""],
            ["3 · Expand", "Outcome-linked", "Optionally tie part of the fee to the agreed metric — claims auto-adjudicated, hours saved, fraud caught. Telemetry on workflows and decisions served drives the annual expansion.", ""]].map(([chip, t, d, note]) => (
            <div key={chip} className="card p-6">
              <span className="chip">{chip}</span>
              <div className="mt-4 text-lg font-semibold tracking-tight">{t}</div>
              <p className="mt-2.5 text-[15px] leading-relaxed text-muted">{d}</p>
              {note && <div className="mt-3 text-sm text-muted/80">{note}</div>}
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button className="btn-primary" onClick={() => setEngageOpen(true)}>
            Get an indicative quote <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* CTA / FORM */}
      <section className="grid gap-8 lg:grid-cols-[3fr_2fr]">
        <AccessForm />
        <div className="card flex flex-col justify-between p-7">
          <div>
            <Label>Who we are</Label>
            <h3 className="text-2xl font-bold tracking-tight">Redpill Software</h3>
            <p className="mt-4 text-[15px] leading-relaxed text-muted">
              A software consultancy that builds and operates production AI and data systems. Ontoligent is our flagship
              product — the team that built the technology can also run it for you.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5 text-sm">
              {["Consultancy services", "Ontology & knowledge engineering", "AI architecture & deployment", "Managed operation"]
                .map((c) => <span key={c} className="chip">{c}</span>)}
            </div>
          </div>
          <a href="https://redpill.software" target="_blank" rel="noopener" className="btn-ghost mt-6 w-fit">
            <FileDown size={15} /> Visit redpill.software
          </a>
        </div>
      </section>

      <PricingEngagement open={engageOpen} plan="enterprise" onClose={() => setEngageOpen(false)} />
    </div>
  );
}
